import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useCustomToast } from 'design-system';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { UseQueryOptions, UseQueryResult } from 'react-query';
import type { ProductBrandUsage } from './types';
import { productBrandsEndpoint } from './use-get-product-brands-api';

interface ResponseData {
  data: ProductBrandUsage[];
}

type TData = ResponseData;
type TError = AxiosError;
type TQueryKey = [string];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

/**
 * GET api/v1/cms/product-brands/usage
 * Usage counts per brand across live master products, grouped case-insensitively
 * on the BE. Feeds the Usage column of the brands management table.
 */
export function useGetProductBrandUsageApi(options?: Options): ReturnType {
  const toast = useCustomToast();
  const isLoggedIn = useIsLoggedIn();
  const { enabled: callerEnabled, ...restOptions } = options ?? {};

  const queryFn = async () => {
    try {
      const { data } = await http.get(`${productBrandsEndpoint}/usage`);

      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useQueryWrapper([`${productBrandsEndpoint}/usage`], queryFn, {
    enabled: (callerEnabled ?? true) && isLoggedIn,
    staleTime: 0,
    retry: false,
    ...restOptions,
  });
}

/** Usage count for one brand name (the API groups by lower-cased trimmed name). */
export function usageForBrand(usage: ProductBrandUsage[] | undefined, name: string): number {
  return usage?.find((entry) => entry.value === name.trim().toLowerCase())?.count ?? 0;
}
